'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/neural-nets'

const XorDemo = dynamic(() => import('../_components/widgets/NNWidgets').then(m => m.XorDemo), { ssr: false })

function XorTable() {
  return (
    <div className="flex flex-wrap items-center gap-8">
      <table className="gd-table !w-auto">
        <thead>
          <tr>
            <th>x₁</th>
            <th>x₂</th>
            <th>XOR</th>
          </tr>
        </thead>
        <tbody>
          {[
            [0, 0, 0],
            [0, 1, 1],
            [1, 0, 1],
            [1, 1, 0],
          ].map(r => (
            <tr key={r.join()}>
              <td className="gd-num" style={{ color: 'var(--gd-blue)' }}>
                {r[0]}
              </td>
              <td className="gd-num" style={{ color: 'var(--gd-blue)' }}>
                {r[1]}
              </td>
              <td className="gd-num font-bold" style={{ color: r[2] ? 'var(--gd-amber)' : 'var(--gd-blue)' }}>
                {r[2]}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <svg viewBox="0 0 160 160" className="w-[160px]" role="img" aria-label="XOR points: no straight line separates the classes">
        <rect x={10} y={10} width={140} height={140} fill="none" stroke="var(--gd-rule-2)" />
        {[
          [0, 0, 0],
          [0, 1, 1],
          [1, 0, 1],
          [1, 1, 0],
        ].map(([a, b, y]) => (
          <circle key={`${a}${b}`} cx={30 + a * 100} cy={130 - b * 100} r={11} fill={y ? 'var(--gd-amber)' : 'var(--gd-blue)'} />
        ))}
        <line x1={10} y1={150} x2={150} y2={40} stroke="var(--gd-pink)" strokeWidth={2} strokeDasharray="5 5" />
        <text x={80} y={92} textAnchor="middle" fontSize={22} fill="var(--gd-pink)">
          ?
        </text>
      </svg>
    </div>
  )
}

export default function LayersAndMlps() {
  return (
    <Lesson>
      <Step gate="q-xor">
        <H>The problem one neuron can’t solve</H>
        <P>
          <strong>XOR</strong> (“exclusive or”) outputs 1 when exactly one of its two inputs is 1. Plot the four cases, and
          you’ll notice something awkward: the amber points sit on opposite corners. No single straight line can put both amber
          points on one side and both blue on the other.
        </P>
        <Figure n="7.2" caption="XOR: the answer is 1 when the inputs differ. Try to imagine one straight line separating amber from blue — it can’t be done.">
          <XorTable />
        </Figure>
        <Quiz
          id="q-xor"
          question="Since one neuron draws one straight line, can a single neuron learn XOR?"
          options={[
            { label: 'No — the classes aren’t separable by any single straight line.', correct: true },
            { label: 'Yes, with a big enough weight.', why: 'Bigger weights make the line sharper, not curved.' },
            { label: 'Yes, with the ReLU activation.', why: 'Any single neuron splits the plane along one line, whatever its activation.' },
          ]}
          explain="This exact limitation, published in 1969, helped trigger the first ‘AI winter’. The fix was known in principle — more layers — but nobody could train them yet."
        />
      </Step>

      <Step>
        <H>Add a hidden layer</H>
        <P>
          Put two neurons in between the inputs and the output. These <strong>hidden</strong> neurons each draw their own line,
          and their outputs become new coordinates for the final neuron. If they’re chosen well, they move the four points
          around so that a single line <em>can</em> separate them.
        </P>
        <Figure n="7.3" wide caption="Left: the network’s decision regions in the original input space. Right: where each point lands after the hidden layer. Train it — watch the hidden layer bend space until the output neuron’s straight line (dashed) separates the colours.">
          <XorDemo />
        </Figure>
        <Callout kind="plain">
          Each layer re-arranges the data so the next layer’s job is easier. Stack enough layers and you can untangle almost
          anything.
        </Callout>
      </Step>

      <Step gate="q-layer">
        <H>The multi-layer perceptron</H>
        <P>
          A network of layers like this is called a <strong>multi-layer perceptron</strong> (MLP), or a fully-connected network.
          Every layer does the same thing: a matrix multiply (all the neurons’ dot products at once — Module 3!), add a bias,
          apply the activation.
        </P>
        <MB>{'\\mathbf{h} = f(W_1 \\blue{\\mathbf{x}} + \\mathbf{b}_1) \\qquad \\amber{\\hat{y}} = \\sigma(W_2 \\mathbf{h} + \\mathbf{b}_2)'}</MB>
        <P>
          Running the input through every layer to get an output is called the <strong>forward pass</strong>.
        </P>
        <Quiz
          id="q-layer"
          question="A layer has 3 inputs and 4 neurons. What shape is its weight matrix W?"
          options={[
            { label: '4 × 3 — one row of 3 weights per neuron', correct: true },
            { label: '3 × 3', why: 'There are 4 neurons, each needing its own row of weights.' },
            { label: '4 × 4', why: 'Each neuron has one weight per input — and there are 3 inputs.' },
          ]}
          explain="W·x with W being 4×3 and x being 3 numbers gives 4 outputs — one per neuron. (Plus 4 biases: 16 parameters in total.)"
        />
      </Step>

      <Step gate="xor-forward">
        <H>A forward pass by hand</H>
        <P>Here’s a network with weights chosen by hand to solve XOR. Write the layer function and watch it work.</P>
        <Exercise
          id="xor-forward"
          title="Forward pass through a 2-2-1 network"
          {...EX.xorForward}
          hints={[
            <>
              <C>{'W.map((row, j) => sigmoid(vec.dot(row, x) + b[j]))'}</C> — one output per row.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-universal">
        <H>How powerful are these things?</H>
        <P>
          A famous result, the <strong>universal approximation theorem</strong>, says that a network with just one hidden layer
          — given enough neurons — can approximate any reasonable function as closely as you like. In practice,{' '}
          <em>deeper</em> networks (more layers, fewer neurons each) learn far more efficiently: early layers find simple
          patterns, later layers combine them into complex ones.
        </P>
        <Quiz
          id="q-universal"
          question="Why don’t we just remove the activation functions to make networks simpler?"
          options={[
            { label: 'Without them, any number of layers collapses into a single linear layer — just a straight line.', correct: true },
            { label: 'Activations make training faster.', why: 'They actually add some cost. They’re essential for expressive power.' },
            { label: 'No reason — they’re optional.', why: 'W₂(W₁x) = (W₂W₁)x: a product of matrices is just another matrix. Without bends, depth adds nothing.' },
          ]}
        />
        <Callout kind="why">
          We hand-picked the XOR weights. For a real network with millions of weights, we need to learn them with gradient
          descent — which means computing the gradient of the loss with respect to every weight in every layer. That’s
          backpropagation, next.
        </Callout>
      </Step>
    </Lesson>
  )
}
